import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clearToken, getToken, notifyAuthChanged } from '@/lib/auth-storage';
import { clearSaleSmartlyUser } from '@/lib/salesmartly';
import { DEFAULT_AVATAR_URL } from '@/constants/avatar';
import { profileApi } from '@/api/auth';

const navLinks = [
  { label: '首页', path: '/', icon: 'ri-home-4-line', activeIcon: 'ri-home-4-fill' },
  { label: '查询服务', path: '/services', icon: 'ri-search-2-line', activeIcon: 'ri-search-2-fill' },
  { label: '我的订单', path: '/orders', icon: 'ri-file-list-3-line', activeIcon: 'ri-file-list-3-fill' },
  { label: '帮助中心', path: '/help', icon: 'ri-customer-service-2-line', activeIcon: 'ri-customer-service-2-fill' },
];

function UserAvatar({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  return (
    <div className={`${sizeClass} rounded-full bg-gray-100 overflow-hidden flex-shrink-0 ring-1 ring-gray-100`}>
      <img src={DEFAULT_AVATAR_URL} alt="avatar" className="w-full h-full object-cover" />
    </div>
  );
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authed, setAuthed] = useState(() => !!getToken());
  const [userName, setUserName] = useState('');
  const location = useLocation();

  useEffect(() => {
    const sync = () => setAuthed(!!getToken());
    window.addEventListener('mh-auth-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('mh-auth-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!authed) {
      setUserName('');
      return;
    }
    profileApi()
      .then((res) => {
        if (res.code === 0 && res.data?.userName) {
          setUserName(res.data.userName);
        }
      })
      .catch(() => {});
  }, [authed]);

  const handleLogout = () => {
    clearToken();
    clearSaleSmartlyUser();
    notifyAuthChanged();
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div className="h-[76px] md:h-[88px]" />
      <header
        className={`z-50 fixed transition-all duration-300 bg-white border-b border-gray-100 ${
          scrolled
            ? 'top-3 left-3 right-3 rounded-2xl shadow-xl border border-gray-200'
            : 'top-0 left-0 right-0 rounded-none shadow-none'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[76px] md:h-[88px]">

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2.5 flex-shrink-0">
              <img
                src="/new_logo.png"
                alt="查191"
                className="h-14 md:h-[72px] w-auto object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path || (link.path === '/help' && location.pathname.startsWith('/help'));
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                      isActive
                        ? 'text-gray-900 bg-gray-100'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${isActive ? link.activeIcon : link.icon} text-base`} />
                    </div>
                    <span>{link.label}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-gray-900 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-2">
              {/* Desktop auth */}
              <div className="hidden md:flex items-center space-x-2">
                {authed ? (
                  <>
                    <Link
                      to="/account"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-gray-50"
                    >
                      <UserAvatar />
                      <span>{userName || '用户中心'}</span>
                    </Link>
                    <Link
                      to="/affiliate"
                      className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-gray-50"
                    >
                      推广好友
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-gray-50"
                    >
                      退出
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
                    >
                      立即注册
                    </Link>
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-gray-50"
                    >
                      登录
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile: login + hamburger */}
              <div className="md:hidden flex items-center space-x-2">
                {authed ? (
                  <>
                    <Link
                      to="/account"
                      className="flex items-center gap-1.5 px-1.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <UserAvatar size="sm" />
                      <span className="max-w-[56px] truncate">{userName || '我的'}</span>
                    </Link>
                    <Link
                      to="/affiliate"
                      className="px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      推广好友
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      立即注册
                    </Link>
                    <Link
                      to="/login"
                      className="px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      登录
                    </Link>
                  </>
                )}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  aria-label="菜单"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl text-gray-700`} />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path || (link.path === '/help' && location.pathname.startsWith('/help'));
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${isActive ? link.activeIcon : link.icon} text-lg`} />
                  </div>
                  <span>{link.label}</span>
                  {isActive && (
                    <div className="ml-auto w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-gray-900" />
                    </div>
                  )}
                </Link>
              );
            })}
            <div className="pt-2 border-t border-gray-100 space-y-2">
              {authed ? (
                <>
                  <Link
                    to="/account"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-100 text-gray-900 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    <UserAvatar size="sm" />
                    用户中心
                  </Link>
                  <Link
                    to="/affiliate"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-100 text-gray-900 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    <i className="ri-gift-line" />
                    推广好友
                  </Link>
                  <button
                    type="button"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center w-full py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    退出登录
                  </button>
                </>
              ) : (
                <Link
                  to="/register"
                  className="flex items-center justify-center w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  免费注册账号
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-50">
        <div className="grid grid-cols-4 h-16">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path || (link.path === '/help' && location.pathname.startsWith('/help'));
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex flex-col items-center justify-center space-y-1 transition-all duration-200 cursor-pointer ${
                  isActive ? 'text-gray-900' : 'text-gray-400 hover:text-gray-700'
                }`}
              >
                <div className="w-6 h-6 flex items-center justify-center relative">
                  <i className={`${isActive ? link.activeIcon : link.icon} text-xl`} />
                  {isActive && (
                    <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-gray-900 rounded-full" />
                  )}
                </div>
                <span className="text-[10px] font-medium">
                  {link.label.replace('查询服务', '查询').replace('帮助中心', '帮助').replace('我的订单', '订单')}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
